<template>
  <footer class="footer-section">
    <!-- CTA Area -->
    <div class="footer-section__cta">
      <div class="footer-section__cta-container">
        <h2 class="footer-section__cta-title">
          ¿Listo para revolucionar tu email marketing?
        </h2>
        
        <div class="footer-section__form">
          <div class="footer-section__input-group">
            <EmbeddedSignupForm />
          </div>
          <p class="footer-section__form-note">
            <i class="pi pi-check-circle"></i>
            Sin tarjeta de crédito · 1,000 validaciones gratis
          </p>
        </div>
      </div>
    </div>
    
    <!-- Links Area -->
    <div class="footer-section__links">
      <div class="footer-section__links-container">
        <div class="footer-section__brand">
          <img 
            :src="logoUrl" 
            alt="ThunderLabs" 
            class="footer-section__logo"
          />
        </div>
        
        <nav class="footer-section__nav">
          <a href="#features" class="footer-section__link">Producto</a>
          <span class="footer-section__separator">|</span>
          <a href="#resources" class="footer-section__link">Recursos</a>
          <span class="footer-section__separator">|</span>
          <a href="#legal" class="footer-section__link">Legal</a>
          <span class="footer-section__separator">|</span>
          <a href="mailto:hola@thunderlabs.cl" class="footer-section__link">Contacto</a>
        </nav>
      </div>
    </div>
    
    <!-- Bottom Bar -->
    <div class="footer-section__bottom">
      <div class="footer-section__bottom-container">
        <p class="footer-section__copyright">
          © 2026 ThunderLabs
        </p>
        <div class="footer-section__legal-links">
          <a href="/privacy" class="footer-section__legal-link">Privacidad</a>
          <span class="footer-section__dot">·</span>
          <a href="/terms" class="footer-section__legal-link">Términos</a>
          <span class="footer-section__dot">·</span>
          <a href="/data-deletion" class="footer-section__legal-link">Eliminación de datos</a>
        </div>
        <p class="footer-section__tagline">
          Hecho con <i class="pi pi-bolt"></i> en Latinoamérica 🇨🇱
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref } from 'vue';
import EmbeddedSignupForm from './EmbeddedSignupForm.vue';

const logoUrl = '/images/thunder_labs_logo.svg';
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

.footer-section {
  width: 100%;
}

// ===========================================
// CTA AREA
// ===========================================

.footer-section__cta {
  background: linear-gradient(135deg, $tl-jet 0%, lighten($tl-jet, 8%) 100%);
  padding: $tl-space-20 $tl-space-6;
  text-align: center;
}

.footer-section__cta-container {
  max-width: 600px;
  margin: 0 auto;
}

.footer-section__cta-title {
  font-size: $tl-font-size-3xl;
  font-weight: $tl-font-weight-bold;
  color: $tl-white;
  margin: 0 0 $tl-space-3;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: $tl-font-size-2xl;
  }
}

.footer-section__cta-subtitle {
  font-size: $tl-font-size-lg;
  color: rgba($tl-white, 0.7);
  margin: 0 0 $tl-space-8;
}

.footer-section__form {
  width: 100%;
}

.footer-section__input-group {
  display: flex;
  gap: $tl-space-3;
  max-width: 500px;
  margin: 0 auto $tl-space-4;
  width: 100%;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
}

.footer-section__form-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $tl-space-2;
  font-size: $tl-font-size-sm;
  color: rgba($tl-white, 0.6);
  margin: 0;
  
  .pi {
    color: $tl-success;
  }
}

// ===========================================
// LINKS AREA
// ===========================================

.footer-section__links {
  background: $tl-jet;
  padding: $tl-space-8 $tl-space-6;
  border-top: 1px solid rgba($tl-white, 0.1);
}

.footer-section__links-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $tl-space-6;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
}

.footer-section__logo {
  height: 32px;
  width: auto;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.footer-section__nav {
  display: flex;
  align-items: center;
  gap: $tl-space-4;
  flex-wrap: wrap;
  justify-content: center;
}

.footer-section__link {
  color: rgba($tl-white, 0.8);
  text-decoration: none;
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-medium;
  transition: $tl-transition-default;
  
  &:hover {
    color: $tl-white;
  }
}

.footer-section__separator {
  color: rgba($tl-white, 0.3);
  font-size: $tl-font-size-sm;
}

// ===========================================
// BOTTOM BAR
// ===========================================

.footer-section__bottom {
  background: darken($tl-jet, 3%);
  padding: $tl-space-5 $tl-space-6;
  border-top: 1px solid rgba($tl-white, 0.05);
}

.footer-section__bottom-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $tl-space-4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
}

.footer-section__copyright {
  font-size: $tl-font-size-sm;
  color: rgba($tl-white, 0.5);
  margin: 0;
}

.footer-section__legal-links {
  display: flex;
  align-items: center;
  gap: $tl-space-2;
}

.footer-section__legal-link {
  color: rgba($tl-white, 0.5);
  text-decoration: none;
  font-size: $tl-font-size-sm;
  transition: $tl-transition-default;
  
  &:hover {
    color: rgba($tl-white, 0.8);
  }
}

.footer-section__dot {
  color: rgba($tl-white, 0.3);
}

.footer-section__tagline {
  font-size: $tl-font-size-sm;
  color: rgba($tl-white, 0.5);
  margin: 0;
  
  .pi {
    color: $tl-primary;
  }
}
</style>
